import clsx from "clsx";
import { useEffect, useState } from "react";

import type {
  Filters,
  OptionItem,
  PerformanceReportSelectOptions,
  SourceType,
} from "../types";

import { Button as ButtonV2 } from "../../Button";
import { useIsMobile } from "../../hooks/useIsMobile";
import { ModalFooterSkeleton, ModalFormSkeleton } from "../../ModalSkeleton";
import { MultiSelect } from "../../MultiSelect";
import { ResponsiveModal } from "../../ResponsiveModal";
import { Typo } from "../../Typo";
import { usePerformanceReportTranslations } from "../hooks/usePerformanceReportTranslations";
import { usePerformanceReportAnalyticsContext } from "../providers/PerformanceReportAnalyticsProvider";

export const EMPTY_FILTERS: Filters = {
  sourceType: [],
  landingPage: [],
  bannerGroup: [],
  rebateProgram: [],
  country: [],
  campaign: [],
};

// Analytics link_text must show the human-readable option names, not the
// underlying ids/values the filter state stores (PART-4116 QA).
const toLabels = (values: string[], options: OptionItem[]): string[] => {
  const labelByValue = new Map(options.map((o) => [o.value, o.label]));
  return values.map((v) => labelByValue.get(v) ?? v);
};

type MultiOption = { label: string; value: string };

type FieldProps = {
  label: string;
  placeholder: string;
  options: OptionItem[];
  value: string[];
  onChange: (next: string[]) => void;
  isMobile: boolean;
  hideDivider?: boolean;
};

const MultiField = ({
  label,
  placeholder,
  options,
  value,
  onChange,
  isMobile,
  hideDivider,
}: FieldProps) => {
  const optionByValue = new Map(options.map((o) => [o.value, o]));
  const selected: MultiOption[] = value.map(
    (v) => optionByValue.get(v) ?? { value: v, label: v },
  );

  return (
    <MultiSelect
      label={label}
      modal={!isMobile}
      variant={isMobile ? "expandable" : "dropdown"}
      hideDivider={hideDivider}
      options={options}
      selected={selected}
      placeholder={placeholder}
      onChange={(next) => onChange(next.map((o) => o.value))}
    />
  );
};

type PerformanceFilterModalProps = {
  isOpen: boolean;
  initial: Filters;
  selectOptions: PerformanceReportSelectOptions;
  onApply: (filters: Filters) => void;
  onClose: () => void;
  /** While true, show a skeleton in place of the fields + footer buttons. */
  isOptionsLoading?: boolean;
};

/**
 * Filter modal — reuses the ResponsiveModal + MultiSelect pattern from the
 * referral-links / marketing-banner filters. Landing page and Banner group
 * progressively reveal based on the Source type(s) chosen (AC-3).
 */
export const PerformanceFilterModal = ({
  isOpen,
  initial,
  selectOptions,
  onApply,
  onClose,
  isOptionsLoading = false,
}: PerformanceFilterModalProps) => {
  const translations = usePerformanceReportTranslations();
  const isMobile = useIsMobile();
  const analytics = usePerformanceReportAnalyticsContext();
  const [draft, setDraft] = useState<Filters>(initial);

  // Re-seed the draft from the live filters whenever the modal (re)opens.
  useEffect(() => {
    if (isOpen) setDraft(initial);
  }, [isOpen, initial]);

  const set = <K extends keyof Filters>(key: K, val: Filters[K]) =>
    setDraft((d) => ({ ...d, [key]: val }));

  const toggleSourceType = (opt: SourceType) => {
    analytics?.trackFilterSourceEvent?.(opt);
    setDraft((currentDraft) => {
      const sourceType = currentDraft.sourceType.includes(opt)
        ? currentDraft.sourceType.filter((v) => v !== opt)
        : [...currentDraft.sourceType, opt];

      return {
        ...currentDraft,
        sourceType,
      };
    });
  };

  const hasLinks = draft.sourceType.includes("Links");
  const hasBanners = draft.sourceType.includes("Banners");

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      isMobile={isMobile}
      mobileSheetDraggable
      title={translations.filterTitle}
      contentOverflow={isMobile ? "auto" : "visible"}
      footer={
        isOptionsLoading ? (
          <ModalFooterSkeleton isMobile={isMobile} />
        ) : (
          <div className="flex gap-3 lg:justify-end">
            <ButtonV2
              variant="outline"
              size="md"
              className="flex-1 lg:flex-none"
              onClick={() => {
                analytics?.trackFilterClearEvent?.();
                setDraft(EMPTY_FILTERS);
              }}
            >
              {isMobile ? translations.mobileReset : translations.filterClear}
            </ButtonV2>
            <ButtonV2
              variant="brand"
              size="md"
              className="flex-1 lg:flex-none"
              onClick={() => {
                const next: Filters = {
                  ...draft,
                  landingPage: hasLinks ? draft.landingPage : [],
                  bannerGroup: hasBanners ? draft.bannerGroup : [],
                };
                analytics?.trackFilterApplyEvent?.({
                  sourceType: next.sourceType,
                  landingPage: toLabels(
                    next.landingPage,
                    selectOptions.landingPage,
                  ),
                  bannerGroup: toLabels(
                    next.bannerGroup,
                    selectOptions.bannerGroup,
                  ),
                  rebateProgram: toLabels(
                    next.rebateProgram,
                    selectOptions.rebateProgram,
                  ),
                  country: toLabels(next.country, selectOptions.country),
                  campaign: toLabels(next.campaign, selectOptions.campaign),
                });
                onApply(next);
              }}
            >
              {translations.filterApply}
            </ButtonV2>
          </div>
        )
      }
    >
      {isOptionsLoading ? (
        <ModalFormSkeleton />
      ) : (
        <>
          <div
            className={clsx(isMobile && "border-b border-stroke-primary pb-6")}
          >
            <Typo
              element="span"
              type="labelSM"
              textColor="text-text-primary"
              className="mb-2 block"
            >
              {translations.filterSource}
            </Typo>
            <div className="flex flex-wrap gap-2">
              {selectOptions.sourceType.map((opt) => {
                const on = draft.sourceType.includes(opt);
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => toggleSourceType(opt)}
                    className={clsx(
                      "rounded-lg px-5 py-2.5 transition-colors",
                      on
                        ? "bg-surface-inverse-primary text-text-inverse-primary"
                        : "bg-surface-tonal-default text-text-primary hover:bg-surface-tonal-bold",
                    )}
                  >
                    <Typo
                      element="span"
                      type="labelSM"
                      textColor={
                        on ? "text-text-inverse-primary" : "text-text-primary"
                      }
                    >
                      {translations.filterSourceLabels[opt]}
                    </Typo>
                  </button>
                );
              })}
            </div>
          </div>

          {hasLinks && (
            <MultiField
              label={translations.filterLandingPageLabel}
              placeholder={translations.filterLandingPagePlaceholder}
              options={selectOptions.landingPage}
              value={draft.landingPage}
              onChange={(v) => {
                analytics?.trackFilterLandingPageEvent?.(
                  toLabels(v, selectOptions.landingPage),
                );
                set("landingPage", v);
              }}
              isMobile={isMobile}
            />
          )}
          {hasBanners && (
            <MultiField
              label={translations.filterBannerGroupLabel}
              placeholder={translations.filterBannerGroupPlaceholder}
              options={selectOptions.bannerGroup}
              value={draft.bannerGroup}
              onChange={(v) => {
                analytics?.trackFilterBannerGroupEvent?.(
                  toLabels(v, selectOptions.bannerGroup),
                );
                set("bannerGroup", v);
              }}
              isMobile={isMobile}
            />
          )}
          <MultiField
            label={translations.filterRebateProgramLabel}
            placeholder={translations.filterRebateProgramPlaceholder}
            options={selectOptions.rebateProgram}
            value={draft.rebateProgram}
            onChange={(v) => {
              analytics?.trackFilterRebateProgramEvent?.(
                toLabels(v, selectOptions.rebateProgram),
              );
              set("rebateProgram", v);
            }}
            isMobile={isMobile}
          />
          <MultiField
            label={translations.filterCountryLabel}
            placeholder={translations.filterCountryPlaceholder}
            options={selectOptions.country}
            value={draft.country}
            onChange={(v) => {
              analytics?.trackFilterCountryEvent?.(
                toLabels(v, selectOptions.country),
              );
              set("country", v);
            }}
            isMobile={isMobile}
            hideDivider={selectOptions.campaign.length === 0}
          />
          {/* The host only supplies campaign options while
              `part-4165-campaigns` is on — an empty list is the flag-off case
              and hides the facet. */}
          {selectOptions.campaign.length > 0 && (
            <MultiField
              label={translations.filterCampaignLabel}
              placeholder={translations.filterCampaignPlaceholder}
              options={selectOptions.campaign}
              value={draft.campaign}
              onChange={(v) => set("campaign", v)}
              isMobile={isMobile}
              hideDivider
            />
          )}
        </>
      )}
    </ResponsiveModal>
  );
};
